<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { toolHref } from '../model/toolLinks'
import ToolIcon from './ToolIcon.vue'
import type { ToolEntry } from '../model/tools'

const props = defineProps<{ tool: ToolEntry }>()

const { t, locale } = useI18n()

// Recomputed on a language switch, which happens without a reload: a stale href
// here would drop the visitor back into English.
const href = computed(() => toolHref(props.tool, locale.value))
</script>

<template>
  <article class="card" :data-status="tool.status">
    <header class="card__head">
      <span class="card__icon">
        <ToolIcon :id="tool.id" />
      </span>
      <span class="card__status" :data-status="tool.status">
        {{ t(`home.status.${tool.status}`) }}
      </span>
    </header>

    <h3 class="card__name">{{ t(tool.nameKey) }}</h3>
    <p class="card__summary">{{ t(tool.summaryKey) }}</p>

    <ul class="card__highlights">
      <li v-for="key in tool.highlightKeys" :key="key">{{ t(key) }}</li>
    </ul>

    <footer class="card__foot">
      <a
        v-if="href"
        class="card__cta"
        :href="href"
        :aria-label="t('home.tools.openNamed', { tool: t(tool.nameKey) })"
      >
        {{ t('home.tools.open') }}
        <span class="card__arrow" aria-hidden="true">→</span>
      </a>
      <p v-else class="card__pending">{{ t('home.tools.notYet') }}</p>
    </footer>
  </article>
</template>

<style scoped>
.card {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: var(--space-6);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  box-shadow: var(--shadow-sm);
  transition:
    border-color var(--transition-fast),
    box-shadow var(--transition-fast),
    transform var(--transition-fast);
}

/* The whole card is the link's target, so it should feel like one. */
.card:has(.card__cta):hover {
  border-color: var(--color-accent);
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.card:has(.card__cta:focus-visible) {
  outline: 2px solid var(--color-focus-ring);
  outline-offset: 2px;
}

/* Nothing to visit yet: present, but visibly not ready. */
.card[data-status='planned'] {
  border-style: dashed;
  background: var(--color-surface-sunken);
  box-shadow: none;
}

.card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-2);
}

.card__icon {
  display: grid;
  place-items: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: var(--radius-md);
  background: var(--color-accent-subtle);
  color: var(--color-accent);
}

.card[data-status='planned'] .card__icon {
  background: var(--color-surface);
  color: var(--color-text-muted);
}

.card__status {
  padding: 0.125rem var(--space-2);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  color: var(--color-text-muted);
}

.card__status[data-status='available'] {
  border-color: var(--color-success);
  color: var(--color-success);
}

.card__status[data-status='development'] {
  border-color: var(--color-accent);
  color: var(--color-accent);
}

.card__name {
  margin: var(--space-4) 0 0;
  font-size: var(--text-lg);
}

.card__summary {
  margin: var(--space-2) 0 0;
  color: var(--color-text-muted);
}

.card__highlights {
  margin: var(--space-4) 0 0;
  padding: 0;
  list-style: none;
  font-size: var(--text-sm);
}

.card__highlights li {
  position: relative;
  padding-left: var(--space-6);
}

/* A tick drawn in CSS, so it takes the theme's colour without an icon per row. */
.card__highlights li::before {
  content: '';
  position: absolute;
  left: 0.2rem;
  top: 0.3rem;
  width: 0.35rem;
  height: 0.65rem;
  border: solid var(--color-success);
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.card[data-status='planned'] .card__highlights li::before {
  border-color: var(--color-text-muted);
}

.card__highlights li + li {
  margin-top: var(--space-2);
}

/* Pushed to the bottom so a row of cards lines up whatever the length of each
   description. */
.card__foot {
  margin-top: auto;
  padding-top: var(--space-6);
}

.card__cta {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-md);
  background: var(--color-accent);
  color: var(--color-text-inverted);
  font-size: var(--text-sm);
  font-weight: 600;
  text-decoration: none;
}

/* Stretches the one real link over the whole card: one tab stop, one accessible
   name, and the full card clickable. */
.card__cta::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
}

.card__cta:focus-visible {
  outline: none;
}

.card:hover .card__cta {
  background: var(--color-accent-hover);
}

.card__arrow {
  transition: transform var(--transition-fast);
}

.card:hover .card__arrow {
  transform: translateX(3px);
}

.card__pending {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}
</style>
